import { Color3, Matrix, Vector3 } from '@babylonjs/core/Maths/math';
import { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { ShadowOnlyMaterial } from '@babylonjs/materials/shadowOnly';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
// import { ReflectionProbe } from '@babylonjs/core/Probes/reflectionProbe';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';

import cloneDeep from 'lodash/cloneDeep';
import clone from 'lodash/clone';
import { vector3Json } from '../Content/content';
import { hexToRgbBabylon } from '../../Utils/color';
import { PatternLight } from '../Content/Pattern/patternLight';
import { SystemLayer } from '../System/systemLayer';
import { EXPORT_FILTER_PREFIX, projectInterface } from '../Tool/interface';
import { IVector3 } from '~/Utils/math';

export interface IEnvironmentSkyOptions {
    visibleSky?: boolean,
    skycolor?: number[],
    skyblur?: number,
    skybrightness?: number,
    skyorientation?: number,
    lightOrientation?: IVector3,
    shadowGroundVisibility?: number,
    shadowGroundSoftness?: number,
    shadowGroundHeight?: number,
}

export interface IEnvironmentOptions extends IEnvironmentSkyOptions {
    skydiffuse?: string,
    reflection?: boolean,
}

export const environmentSkyJson = {
    visibleSky: 'vs',
    skycolor: 'sc',
    skybrightness: 'sbr',
    skyorientation: 'sor',
    skyblur: 'sbl',
    lightOrientation: {
        dim: 'lo',
        next: vector3Json
    },
    shadowGroundVisibility: 'sgv',
    shadowGroundSoftness: 'sgs',
    shadowGroundHeight: 'sgh',
};

export const environmentAmbientJson = {
    skydiffuse: 'sd',
    reflection: 'r',
};

export const defaultEnvironmentValues: IEnvironmentOptions = {
    visibleSky: false,
    skycolor: [255, 255, 255, 1],
    skybrightness: 1,
    skyorientation: 0,
    skyblur: 0,
    lightOrientation: new Vector3(30, 0, 0),
    shadowGroundVisibility: 0,
    shadowGroundSoftness: 0,
    shadowGroundHeight: 0,
};

// Typescript merge object
export const environmentJson = { ...environmentAmbientJson, ...environmentSkyJson };

projectInterface.environment = environmentJson;

/**
* Manage the environment parameters and options
*/
export class SceneryEvironment {
    /**
    * @ignore
    */
    private _system: SystemLayer;

    /**
    * The big sphere surrounding the scene and on which is set the reflection texture
    */
    public skybox: Mesh;

    private skyboxMaterial: PBRMaterial;

    public currentValues: IEnvironmentOptions = cloneDeep(defaultEnvironmentValues);

    /**
    * @param system System of the 3D scene
    * @param contentsManager Used to manage environment texture on scene content
    */
    constructor(system: SystemLayer) {
        this._system = system;
        this.addSky();
        this.setOption(defaultEnvironmentValues);
    }

    /**
    * @ignore
    */
    private addSky() {
        this.skybox = CreateSphere(`${EXPORT_FILTER_PREFIX}skyboxSphere`, {}, this._system.scene);
        this.skybox.isPickable = false;
        // this.skybox.infiniteDistance = true;
        this.skybox.ignoreCameraMaxZ = true;
        const scale = this._system.visibleWorld + 0.1;
        this.skybox.scaling = new Vector3(scale, scale, scale);
        this.skyboxMaterial = new PBRMaterial('skyboxMaterial', this._system.scene);
        this.skybox.material = this.skyboxMaterial;
        this.skyboxMaterial.disableLighting = true;
        this.skyboxMaterial.twoSidedLighting = true;
        this.skyboxMaterial.backFaceCulling = false;
        this.skyboxMaterial.useRGBColor = false;
        // this.skyboxMaterial.primaryColor = this._options.skyboxColor;
        this.skyboxMaterial.enableNoise = true;

        // fog is use to define the sky color property
        this._system.scene.fogEnabled = false;
        // this._system.scene.fogMode = Scene.FOGMODE_LINEAR;
        this._system.scene.fogMode = Scene.FOGMODE_EXP;

        // Some other babylonjs functions can change autoClear value
        // which is needed with transparent background
        // TODO Use new observable added by Sebavan when upgrade to new BabylonjS version
        // Related forum: https://forum.babylonjs.com/t/prevent-defaultrenderingpipeline-from-changing-scene-autoclear/12954/8
        this._system.scene.registerBeforeRender(() => {
            if (!this.currentValues.visibleSky) {
                this._system.scene.autoClear = true;
            }
        });
    }

    public setCurrentOption(envOptions: IEnvironmentOptions): void {
        const keys = Object.keys(envOptions);
        keys.forEach((key) => {
            this.currentValues[key] = clone(envOptions[key]);
        });
    }

    public getCurrentOption(): IEnvironmentOptions {
        const copyValues = { ...this.currentValues };
        const lO = copyValues.lightOrientation;
        copyValues.lightOrientation = { x: lO.x, y: lO.y, z: lO.z };
        return copyValues;
    }

    /**
    * Set options of the environment
    * @param envOptions Options
    */
    public setOption(envOptions: IEnvironmentOptions): void {
        this.setOptionSky(envOptions);
        this.setOptionLight(envOptions);
        this.setCurrentOption(envOptions);
    }

    // Used for filter change
    private setOptionSky(envOptions: IEnvironmentOptions): void {
        this.setSkyDiffuse(envOptions.skydiffuse);
        this.setVisibleSky(envOptions.visibleSky);
        this.setSkyColor(envOptions.skycolor);
        this.setSkyBlur(envOptions.skyblur);
        this.setSkyBrightness(envOptions.skybrightness);
        this.setSkyOrientation(envOptions.skyorientation);
    }

    private setOptionLight(envOptions: IEnvironmentOptions): void {
        this.setLightOrientation(envOptions.lightOrientation);
        this.setShadowGroundVisibility(envOptions.shadowGroundVisibility);
        this.setShadowGroundSoftness(envOptions.shadowGroundSoftness);
        this.setShadowGroundHeight(envOptions.shadowGroundHeight);
    }

    /**
    * @param envOptions Options
    */
    public checkOption(envOptions: IEnvironmentOptions): void {
        this.checkOptionSky(envOptions);
        this.checkOptionLight(envOptions);
        this.setCurrentOption(envOptions);
    }

    // Used for filter change
    private checkOptionSky(envOptions: IEnvironmentOptions): void {
        if (envOptions.visibleSky !== undefined) this.setVisibleSky(envOptions.visibleSky);
        if (envOptions.skydiffuse !== undefined) this.setSkyDiffuse(envOptions.skydiffuse);
        if (envOptions.skycolor !== undefined) this.setSkyColor(envOptions.skycolor);
        if (envOptions.skyblur !== undefined) this.setSkyBlur(envOptions.skyblur);
        if (envOptions.skybrightness !== undefined) this.setSkyBrightness(envOptions.skybrightness);
        if (envOptions.skyorientation !== undefined) {
            this.setSkyOrientation(envOptions.skyorientation);
        }
    }

    private checkOptionLight(envOptions: IEnvironmentOptions): void {
        if (envOptions.lightOrientation !== undefined) {
            this.setLightOrientation(envOptions.lightOrientation);
        }
        if (envOptions.shadowGroundVisibility !== undefined) {
            this.setShadowGroundVisibility(envOptions.shadowGroundVisibility);
        }
        if (envOptions.shadowGroundSoftness !== undefined) {
            this.setShadowGroundSoftness(envOptions.shadowGroundSoftness);
        }
        if (envOptions.shadowGroundHeight !== undefined) {
            this.setShadowGroundHeight(envOptions.shadowGroundHeight);
        }
    }

    public setVisibleSky(visibleSky: boolean): void {
        this.skybox.isVisible = visibleSky;
        this._system.checkActiveMeshes();
        //! On loadfinish the check is not always working
        setTimeout(() => {
            this._system.checkActiveMeshes();
        }, 100);
    }

    // Used for editor
    private greyBackground = false;

    public getGreyBackground(): boolean {
        return this.greyBackground;
    }

    public setGreyBackground(greyBackground: boolean): void {
        this.greyBackground = greyBackground;
        if (greyBackground) {
            this.setFogColor('#D6D6D6');
            this.setVisibleSky(true);
        } else {
            this._system.scene.fogEnabled = false;
            this.setVisibleSky(true);
        }
    }

    public setFogColor(hexColor: string): void {
        const color = hexToRgbBabylon(hexColor);
        this.setFogColor3(color);
    }

    public setFogColor3(color: Color3): void {
        this._system.scene.fogEnabled = true;
        this._system.scene.fogColor = color;
        this._system.scene.fogMode = Scene.FOGMODE_LINEAR;
        this._system.scene.fogStart = this._system.visibleWorld / 2.1;
        this._system.scene.fogEnd = this._system.visibleWorld / 1.7;
    }

    /**
    * Set the environment texture
    * @param url Url of the new texture to load
    */
    loadingCubeTexture: string;

    public setSkyDiffuse(url: string, callback?: () => void): void {
        if (this.loadingCubeTexture === url) return;
        this.currentValues.skydiffuse = url;
        this.loadingCubeTexture = url;
        if (url === undefined) {
            this.removeEnvironmentTexture();
        } else {
            this._system.loader.getCubeTexture(url, (asset) => {
                if (asset) {
                    this.addEnvironmentTexture(asset.texture);
                } else {
                    this.removeEnvironmentTexture();
                }
                if (callback) callback();
            });
        }
    }

    /**
    * @ignore
    */
    private _environmentTexture: CubeTexture;

    /**
    * @ignore
    */
    private addEnvironmentTexture(environmentTexture: CubeTexture) {
        // Some DDS need that option but we can't know if we need that option or not
        // because it depends on how the file as been created
        environmentTexture.gammaSpace = false;
        this._environmentTexture = environmentTexture;
        this._system.scene.environmentTexture = environmentTexture;

        this.skyboxMaterial.reflectionTexture = environmentTexture.clone();
        this.skyboxMaterial.reflectionTexture.coordinatesMode = Texture.SKYBOX_MODE;

        // Orientation sometimes not applied without the timeout
        setTimeout(() => {
            const cV = this.currentValues;
            if (cV.skyblur !== undefined) this.setSkyBlur(cV.skyblur);
            if (cV.skybrightness !== undefined) this.setSkyBrightness(cV.skybrightness);
            if (cV.skyorientation !== undefined) this.setSkyOrientation(cV.skyorientation);
        }, 10);
    }

    /**
    * @ignore
    */
    private removeEnvironmentTexture() {
        if (this._environmentTexture) {
            this._system.scene.environmentTexture.dispose();
            this._system.scene.environmentTexture = undefined;
            this.skyboxMaterial.reflectionTexture = undefined;
        }
        this.setSkyColor(undefined);
    }

    /**
    * Set the color of the sky
    * @param color new color of the sky
    */
    public setSkyColor(color: number[]): void {
        // Create conflict with tint color used in variant
        // so we remove it for now as variants are more important
        if (color) {
            this.skyboxMaterial.reflectionColor = Color3.FromInts(color[0], color[1], color[2]);
        } else {
            this.skyboxMaterial.reflectionColor = Color3.Black();
        }
    }

    /**
    * Set the brightness of the sky
    * @param brightness New brightness of the sky
    */
    public setSkyBrightness(brightness: number): void {
        if (brightness === undefined) brightness = defaultEnvironmentValues.skybrightness;
        this.currentValues.skybrightness = brightness;
        if (this._environmentTexture) {
            this._system.scene.environmentIntensity = brightness;
        }
    }

    public setSkyOrientation(orientation: number): void {
        if (orientation === undefined) orientation = defaultEnvironmentValues.skyorientation;
        // Saved and set in degrees
        this.currentValues.skyorientation = orientation;
        orientation = (orientation * Math.PI) / 180;
        // In case _environmentTexture is based on image and hence not a cubetexture
        if (this._environmentTexture && this._environmentTexture.setReflectionTextureMatrix) {
            const matrixOrientation = Matrix.RotationY(orientation);
            this._environmentTexture.setReflectionTextureMatrix(matrixOrientation);
            const reflectionTexture = this.skyboxMaterial.reflectionTexture as CubeTexture;
            reflectionTexture.setReflectionTextureMatrix(matrixOrientation);
        }
        this.checkLightOrientation();
    }

    // Test with reflection Probe
    // probe:ReflectionProbe;
    // addEnvironmentTexture (texture?:Texture) {
    //   if (this.reflectionTexture) this.reflectionTexture.dispose();
    //   if (this.probe) this.probe.dispose();
    //   this.probe = new ReflectionProbe("main", 128, this._system.scene);
    //   for (let i = 0; i < this._system.scene.meshes.length; i++) {
    //     let mesh = this._system.scene.meshes[i];
    //     if (mesh.material != undefined) {
    //       // Water materials must not have reflection texture
    //         this.probe.renderList.push(mesh);
    //     }
    //   }
    //   this.probe.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
    //   // this.reflectionTexture = this.probe.cubeTexture;
    //   // this.refractionTexture = this.probe.cubeTexture;
    //   this._system.scene.environmentTexture = this.probe.cubeTexture;
    // }

    /**
    * Set the blur of the sky
    * @param blur New blur of the sky
    */
    public setSkyBlur(blur: number): void {
        if (blur === undefined) blur = defaultEnvironmentValues.skyblur;
        // this.skyboxMaterial.microSurface = 1.0 - blur;
        this.skyboxMaterial.roughness = blur;
    }

    private shadowPlane: Mesh;

    private shadowMaterial: ShadowOnlyMaterial;

    private patternLight: PatternLight

    private addShadowGround() {
        this.shadowPlane = CreatePlane(`${EXPORT_FILTER_PREFIX}shadow_plane`, { size: this._system.visibleWorld }, this._system.scene);
        this.shadowPlane.rotation.x = Math.PI / 2;
        this.shadowMaterial = new ShadowOnlyMaterial('shadow_material', this._system.scene);
        this.shadowPlane.material = this.shadowMaterial;
        this.shadowPlane.receiveShadows = true;
        this.shadowPlane.isPickable = false;

        this.patternLight = new PatternLight(this._system);

        this.patternLight.light.name = 'environment_light';
        this.patternLight.setGroundLight();
        this.checkLightOrientation();

        this.patternLight.setSofnessEnabled(true);
        this.patternLight.setAngleAxis(Vector3.Zero());
        this.patternLight.setIntensity(0);

        this.shadowMaterial.activeLight = this.patternLight.light;
        this._system.checkActiveMeshes();
    }

    public setLightOrientation(rotation: IVector3): void {
        if (!rotation.x) rotation.x = 0;
        if (!rotation.y) rotation.y = 0;
        if (!rotation.z) rotation.z = 0;
        this.currentValues.lightOrientation = rotation;
        this.checkLightOrientation();
    }

    private checkLightOrientation() {
        if (!this.patternLight) return;
        let rotation = this.currentValues.lightOrientation;
        if (!rotation) rotation = defaultEnvironmentValues.lightOrientation;
        rotation = clone(rotation);
        if (this.currentValues.skyorientation) rotation.y -= this.currentValues.skyorientation;
        this.patternLight.setAngleAxis(new Vector3(rotation.x, rotation.y, rotation.z));
    }

    public setShadowGroundVisibility(shadowGroundVisibility: number): void {
        if (shadowGroundVisibility) {
            if (!this.shadowPlane) this.addShadowGround();
            this.shadowPlane.isVisible = true;

            this.patternLight.setShadowDarkness(1);
            this.patternLight.show();
            // let c = 1 - shadowGroundVisibility;
            // this.shadowMaterial.shadowColor = new Color3(c, c, c);
            // this.shadowPlane.visibility = shadowGroundVisibility
            this.shadowMaterial.alpha = shadowGroundVisibility;
        } else if (this.shadowPlane) {
            this.shadowPlane.isVisible = false;
            this.patternLight.setShadowDarkness(0);
            this.patternLight.hide();
        }
        this._system.checkActiveMeshes();
    }

    public setShadowGroundHeight(shadowGroundHeight: number): void {
        if (!this.shadowPlane) return;
        if (!shadowGroundHeight) shadowGroundHeight = 0;
        this.shadowPlane.position.y = shadowGroundHeight;
    }

    public setShadowGroundSoftness(softness: number): void {
        if (!this.patternLight) return;
        this.patternLight.setShadowSoftness(softness);
    }
}
